import { useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Fingerprint,
  Camera,
  Loader2,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  ShieldAlert,
  Clock3,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AuthImage from "@/components/AuthImage";
import { formatMonthYear } from "@/lib/format";

const PUNCH_LABEL = {
  entry: "Entrada",
  break_start: "Saída intervalo",
  break_end: "Volta intervalo",
  exit: "Saída",
};
const PUNCH_ORDER = ["entry", "break_start", "break_end", "exit"];

const timeOf = (iso) =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

function FaceBadge({ record }) {
  const map = {
    approved: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Rosto confere"],
    rejected: ["bg-rose-50 text-rose-800 border-rose-200", "Rosto não confere"],
    no_baseline: ["bg-stone-100 text-stone-600 border-stone-200", "Sem cadastro facial"],
    error: ["bg-amber-50 text-amber-800 border-amber-200", "Falha na análise"],
  };
  const [cls, label] = map[record.face_match] || map.error;
  return (
    <Badge variant="outline" className={`${cls} text-[10px]`} data-testid="punch-face-badge">
      {label}
      {record.face_match === "approved" || record.face_match === "rejected"
        ? ` · ${Math.round((record.face_confidence || 0) * 100)}%`
        : ""}
    </Badge>
  );
}

function GeoBadge({ record }) {
  if (record.latitude == null) {
    return (
      <Badge variant="outline" className="bg-stone-100 text-stone-600 border-stone-200 text-[10px]">
        Sem GPS
      </Badge>
    );
  }
  if (record.distance_m == null) {
    return (
      <Badge variant="outline" className="bg-stone-100 text-stone-600 border-stone-200 text-[10px]">
        GPS ok
      </Badge>
    );
  }
  return record.geo_out_of_range ? (
    <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
      Fora do local · {Math.round(record.distance_m)}m
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
      No local · {Math.round(record.distance_m)}m
    </Badge>
  );
}

export default function TimeClock() {
  const fileRef = useRef(null);
  const [now, setNow] = useState(new Date());
  const [status, setStatus] = useState(null);
  const [gps, setGps] = useState({ state: "idle" });
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [sheet, setSheet] = useState(null);

  const fmtMin = (m) => {
    if (m == null) return "—";
    const sign = m < 0 ? "-" : "";
    const a = Math.abs(Math.round(m));
    return `${sign}${Math.floor(a / 60)}h${String(a % 60).padStart(2, "0")}`;
  };

  const loadSheet = useCallback(async () => {
    try {
      const { data } = await api.get("/timeclock/my-timesheet");
      setSheet(data);
    } catch (err) {
      console.error("my-timesheet failed", err);
    }
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/timeclock/my-status");
      setStatus(data);
    } catch (err) {
      console.error("timeclock status failed", err);
    }
  }, []);

  const captureGps = useCallback(
    () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        setGps({ state: "error" });
        resolve(null);
        return;
      }
      setGps((g) => ({ ...g, state: "loading" }));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const g = {
            state: "ok",
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            acc: pos.coords.accuracy,
          };
          setGps(g);
          resolve(g);
        },
        () => {
          setGps({ state: "error" });
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    }),
    []
  );

  useEffect(() => {
    load();
    loadSheet();
    captureGps();
  }, [load, loadSheet, captureGps]);

  const onPickSelfie = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Imagem maior que 5MB");
      return;
    }
    setSelfie({ file: f, url: URL.createObjectURL(f) });
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmPunch = async () => {
    if (!selfie?.file || !status?.next_type) return;
    setSubmitting(true);
    try {
      const coords = (await captureGps()) || (gps.state === "ok" ? gps : null);
      const fd = new FormData();
      fd.append("file", selfie.file);
      fd.append("purpose", "ponto_selfie");
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.post("/timeclock/punch", {
        selfie_url: up.url,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        accuracy_m: coords?.acc ?? null,
        source: "pwa",
        device_info: navigator.userAgent.slice(0, 200),
      });
      setResult(data);
      setSelfie(null);
      toast.success(data.message);
      load();
      loadSheet();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar ponto");
    } finally {
      setSubmitting(false);
    }
  };

  const records = status?.records || [];
  const recordByType = Object.fromEntries(records.map((r) => [r.type, r]));
  const dayDone = !status?.next_type;

  return (
    <Layout title="Ponto" subtitle="Registre sua jornada com selfie e localização">
      <div className="max-w-2xl mx-auto space-y-5" data-testid="timeclock-page">
        {/* Live clock */}
        <Card className="rounded-xl border-stone-200 bg-stone-950 text-white p-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-400/80 font-body">
            {now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
          </div>
          <div className="font-display text-5xl font-extrabold tabular-nums mt-1" data-testid="timeclock-clock">
            {now.toLocaleTimeString("pt-BR")}
          </div>
          {status?.schedule && (
            <div className="text-xs text-stone-400 font-body mt-2 inline-flex items-center gap-1.5">
              <Clock3 className="h-3.5 w-3.5" />
              Jornada {status.schedule.name}: {status.schedule.entry_time}–{status.schedule.break_start} ·{" "}
              {status.schedule.break_end}–{status.schedule.exit_time} (tolerância{" "}
              {status.schedule.tolerance_minutes} min)
            </div>
          )}
        </Card>

        {status && !status.has_baseline && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm font-body p-3 flex items-start gap-2"
            data-testid="timeclock-no-baseline-warning"
          >
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            Você ainda não possui fotos de referência para o reconhecimento facial. Peça ao seu
            gestor para cadastrá-las no seu perfil — seus registros ficarão sem validação facial.
          </div>
        )}

        {/* 4-punch timeline */}
        <div className="grid grid-cols-4 gap-2" data-testid="timeclock-timeline">
          {PUNCH_ORDER.map((t) => {
            const r = recordByType[t];
            const isNext = status?.next_type === t;
            return (
              <div
                key={t}
                className={`rounded-lg border p-2.5 text-center ${
                  r
                    ? "border-emerald-200 bg-emerald-50"
                    : isNext
                    ? "border-emerald-600 bg-white ring-1 ring-emerald-600"
                    : "border-stone-200 bg-stone-50"
                }`}
                data-testid={`timeclock-slot-${t}`}
              >
                <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body leading-tight">
                  {PUNCH_LABEL[t]}
                </div>
                <div className="font-display font-bold text-sm mt-1 tabular-nums">
                  {r ? timeOf(r.timestamp) : isNext ? "agora" : "—"}
                </div>
                {r?.status === "pending_review" && (
                  <div className="text-[9px] text-amber-700 font-body">em revisão</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Punch action */}
        <Card className="rounded-xl border-stone-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-lg">
                {dayDone ? "Jornada completa" : `Próxima batida: ${status?.next_label || "…"}`}
              </div>
              <div className="text-xs text-stone-500 font-body flex items-center gap-1.5 mt-0.5">
                <MapPin className="h-3.5 w-3.5" />
                {gps.state === "ok"
                  ? `Localização capturada (±${Math.round(gps.acc || 0)}m)`
                  : gps.state === "loading"
                  ? "Obtendo localização…"
                  : gps.state === "error"
                  ? "Sem acesso à localização — o registro será marcado sem GPS"
                  : "Localização pendente"}
              </div>
            </div>
            <Fingerprint className="h-8 w-8 text-emerald-700" />
          </div>

          {!dayDone && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                capture="user"
                onChange={onPickSelfie}
                className="hidden"
                data-testid="timeclock-selfie-input"
              />
              {selfie ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={selfie.url}
                      alt="selfie"
                      className="h-24 w-24 rounded-lg object-cover border border-stone-200"
                      data-testid="timeclock-selfie-preview"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      className="bg-white"
                      data-testid="timeclock-selfie-retake"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" /> Tirar outra
                    </Button>
                  </div>
                  <Button
                    onClick={confirmPunch}
                    disabled={submitting}
                    className="w-full bg-emerald-800 hover:bg-emerald-900 text-white h-12 text-base"
                    data-testid="timeclock-punch-confirm"
                  >
                    {submitting ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Fingerprint className="h-5 w-5 mr-2" />
                    )}
                    {submitting ? "Registrando…" : `Confirmar ${status?.next_label || ""}`}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => fileRef.current?.click()}
                  className="w-full bg-emerald-800 hover:bg-emerald-900 text-white h-12 text-base"
                  data-testid="timeclock-selfie-btn"
                >
                  <Camera className="h-5 w-5 mr-2" /> Tirar selfie e registrar
                </Button>
              )}
            </>
          )}
          {dayDone && (
            <div className="text-sm text-emerald-800 font-body flex items-center gap-2" data-testid="timeclock-day-done">
              <CheckCircle2 className="h-4 w-4" /> Todas as 4 batidas de hoje foram registradas. Bom
              descanso!
            </div>
          )}
        </Card>

        {/* Last punch result */}
        {result && (
          <Card
            className={`rounded-xl p-4 space-y-2 ${
              result.record.status === "pending_review"
                ? "border-amber-200 bg-amber-50"
                : "border-emerald-200 bg-emerald-50"
            }`}
            data-testid="timeclock-result"
          >
            <div className="flex items-center gap-2 font-display font-bold">
              {result.record.status === "pending_review" ? (
                <AlertTriangle className="h-4 w-4 text-amber-700" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              )}
              {result.message}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <FaceBadge record={result.record} />
              <GeoBadge record={result.record} />
              {result.record.late && (
                <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
                  Atraso de {result.record.delay_minutes} min
                </Badge>
              )}
              {result.record.mock_suspect && (
                <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
                  Localização suspeita
                </Badge>
              )}
            </div>
            {result.record.face_summary && (
              <div className="text-xs text-stone-600 font-body">{result.record.face_summary}</div>
            )}
          </Card>
        )}

        {/* Fase D — link to monthly mirror */}
        <div className="flex justify-end -mb-2">
          <Link
            to="/timeclock/mirror"
            className="inline-flex items-center gap-1.5 text-sm font-body text-emerald-800 hover:text-emerald-900 underline underline-offset-4"
            data-testid="timeclock-mirror-link"
          >
            <Clock3 className="h-3.5 w-3.5" /> Ver espelho de ponto do mês
          </Link>
        </div>

        {/* Fase B — bank of hours & overtime summary */}
        {sheet && (sheet.has_schedule || (sheet.totals?.worked_minutes || 0) > 0) && (
          <Card className="rounded-xl border-stone-200 bg-white p-4" data-testid="timeclock-bank-card">
            <div className="font-display font-bold text-sm mb-3">
              Banco de horas & extras — {formatMonthYear(sheet.month)}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">Saldo do mês</div>
                <div
                  className={`font-display font-bold ${
                    sheet.totals.balance_minutes < 0 ? "text-rose-700" : "text-emerald-800"
                  }`}
                  data-testid="bank-month-balance"
                >
                  {fmtMin(sheet.totals.balance_minutes)}
                </div>
              </div>
              {sheet.bank_balance != null && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">Banco acumulado</div>
                  <div
                    className={`font-display font-bold ${
                      sheet.bank_balance < 0 ? "text-rose-700" : "text-emerald-800"
                    }`}
                    data-testid="bank-total-balance"
                  >
                    {fmtMin(sheet.bank_balance)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">Extra 50%</div>
                <div className="font-display font-bold">{fmtMin(sheet.totals.overtime_50)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">Extra 100%</div>
                <div className="font-display font-bold">{fmtMin(sheet.totals.overtime_100)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">Ad. noturno</div>
                <div className="font-display font-bold">{fmtMin(sheet.totals.night_additional_minutes)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">Faltas</div>
                <div className={`font-display font-bold ${sheet.totals.absences > 0 ? "text-rose-700" : ""}`}>
                  {sheet.totals.absences}
                </div>
              </div>
            </div>
            {sheet.policy === "payroll" && (
              <p className="text-[11px] text-stone-500 font-body mt-2">
                Sua jornada envia horas extras para pagamento em folha (sem banco de horas).
              </p>
            )}
          </Card>
        )}

        {/* Today's records */}
        <Card className="rounded-xl border-stone-200 bg-white p-4">
          <div className="font-display font-bold text-sm mb-3">Registros de hoje</div>
          {records.length === 0 ? (
            <div className="text-sm text-stone-500 font-body text-center py-4" data-testid="timeclock-empty">
              Nenhuma batida registrada hoje.
            </div>
          ) : (
            <div className="space-y-2" data-testid="timeclock-records">
              {records.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-3 border border-stone-200 rounded-lg p-2.5"
                  data-testid={`timeclock-record-${r.type}`}
                >
                  <AuthImage
                    src={r.selfie_url}
                    alt="selfie"
                    className="h-11 w-11 rounded-md object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm">
                      {PUNCH_LABEL[r.type]} · {timeOf(r.timestamp)}
                      {r.expected_time && (
                        <span className="text-xs text-stone-400 font-body"> (prev. {r.expected_time})</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <FaceBadge record={r} />
                      <GeoBadge record={r} />
                      {r.late && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
                          +{r.delay_minutes} min
                        </Badge>
                      )}
                    </div>
                  </div>
                  {r.status === "pending_review" && (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] shrink-0">
                      Em revisão
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
